/**
 * 
 * Helpers for editing the free-form price settings that every delivery method stores in
 * `delivery_plans.pricing`. The shape differs per provider, so the editor walks whatever
 * the API returns instead of knowing the schema up front.
 * 
 */

const 

/**
 * 
 * @param { * } value
 * 
 * @returns true for a settings group, false for arrays and leaves
 * 
 */

isGroup = value => value !== null && typeof value === "object" && ! Array.isArray ( value ),

/**
 * 
 * @param { Array } value
 * 
 * @returns true for a bracket table, ie. a list of rows rather than a list of words
 * 
 */

isRowList = value => Array.isArray ( value ) && value.length > 0 && value.every ( isGroup ),

/**
 * 
 * @param { String } key
 * 
 * @returns key turned into a readable label
 * 
 */

humanize = key => key.replace ( /_/g, " " ).replace ( /\b\w/g, character => character.toUpperCase () ),

/**
 * 
 * @param { * } target
 * 
 * @param { Array } path
 * 
 * @param { * } value
 * 
 * @returns a copy of target with the value written at path
 * 
 */

setIn = ( target, path, value ) => {

    if ( path.length === 0 ) return value;

    const [ key, ...rest ] = path;

    if ( Array.isArray ( target ) ) { const copy = [ ...target ];

        copy [ Number ( key ) ] = setIn ( copy [ Number ( key ) ], rest, value );

        return copy;

    }

    const copy = { ...( target || {} ) };

    copy [ key ] = setIn ( copy [ key ], rest, value );

    return copy;

},

/**
 * 
 * @param { * } value
 * 
 * @returns the value with numeric text turned back into numbers and blanks into null
 * 
 * @description Leaves are edited as text so that half-typed numbers such as "5." survive a
 * re-render; this puts the JSON back into the shape the pricing engine expects on save.
 * 
 */

coercePricing = value => {

    if ( Array.isArray ( value ) ) return value.map ( coercePricing );

    if ( isGroup ( value ) ) return Object.fromEntries (

        Object.entries ( value ).map ( ( [ key, child ] ) => [ key, coercePricing ( child ) ] )

    );

    if ( typeof value !== "string" ) return value;

    if ( value.trim () === "" ) return null;

    return /^-?\d+(\.\d+)?$/.test ( value.trim () ) ? Number ( value.trim () ) : value;

},

/**
 * 
 * @param { Array } rows
 * 
 * @returns an empty row shaped like the existing ones
 * 
 */

blankRow = rows => Object.fromEntries (

    Object.keys ( rows [ 0 ] || {} ).map ( key => [ key, null ] )

),

/**
 * 
 * @param { String } key
 * 
 * @returns the settings key as its GraphQL field name, eg. min_price -> minPrice
 * 
 */

camelize = key => key.replace ( /_([a-z0-9])/g, ( _match, character ) => character.toUpperCase () ),

/**
 * 
 * @param { * } value
 * 
 * @param { Boolean } whole
 * 
 * @returns the edited text as a number, or null when the field was left empty
 * 
 */

omit = ( target, key ) => Object.fromEntries (

    Object.entries ( target || {} ).filter ( ( [ name ] ) => name !== key )

),

normalizeCountryCode = value => String ( value || "" ).trim ().toUpperCase (),

isCountryCode = value => /^[A-Z]{2}$/.test ( normalizeCountryCode ( value ) ),

toNumberOrNull = ( value, whole = false ) => {

    if ( value === null || value === undefined || String ( value ).trim () === "" ) return null;

    const parsed = Number ( String ( value ).trim () );

    if ( Number.isNaN ( parsed ) ) return null;

    return whole ? Math.round ( parsed ) : parsed;

};

/** @exports helpers */

export { isGroup, isRowList, humanize, setIn, coercePricing, blankRow, camelize, omit, normalizeCountryCode, isCountryCode, toNumberOrNull };
